<script setup>
import { defineProps, ref } from 'vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'
import DocSection from '@/components/Docs/Section.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'
import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'

defineProps({ anchor: String })

const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)
const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}
</script>

<template>
  <doc-section title="Field schema" :anchor="anchor">
    <template v-slot:left>
      <div class="mb-5">
        <p>
          Returns the queryable field mappings for your accessible sources, merged
          with enrichment annotations. Use it to discover which fields you can
          reference in <a href="#event-search">event search</a> queries and
          <a href="#event-aggregate">aggregations</a>.
        </p>
      </div>

      <div class="mb-5">
        <h4 class="mb-4">Request parameters</h4>
        <div class="param-note">
          <p class="mb-2">
            <code>sources</code> <span class="optional">optional</span>
          </p>
          <p class="mb-0">
            Repeatable query parameter naming the sources to include (e.g.
            <code>?sources=honeypot-1&amp;sources=honeypot-2</code>). Omit it to
            return the schema for every accessible source.
          </p>
        </div>
      </div>

      <div>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">Response attributes</h4>
          <button @click="toggleAllExpansion" class="btn-expand-collapse">
            <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>

        <BasicAttribute name="status" type="string" description="The status of the request's response" />

        <BasicAttribute name="data" type="object" description="The merged schema" :depth="1">
          <BasicAttribute name="fields" type="object" description="Field mappings keyed by field name, describing each field's type" :depth="2" />
          <BasicAttribute name="annotations" type="object" description="Enrichment annotations available per source / log type" :depth="2" />
        </BasicAttribute>
      </div>
    </template>

    <template v-slot:right>
      <endpoint-block
        title="/sources/schema"
        method="GET"
        name="Field Schema"
        query="/sources/schema"
      />

      <json-block title="Response">
{
  "status": "success",
  "data": {
    "fields": {
      "id.orig_h": { "type": "ip" },
      "id.resp_p": { "type": "integer" },
      "proto": { "type": "keyword" },
      "ts": { "type": "date" }
    },
    "annotations": {
      "conn": {
        "id.resp_h": ["geo", "anonymization"]
      }
    }
  }
}
      </json-block>
    </template>
  </doc-section>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}

.param-note {
  padding: 1rem;
  border-radius: var(--radius-lg);
  background-color: var(--surface-sunken);
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.optional {
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-xs);
  background: var(--surface-overlay-2);
  color: var(--text-faint);
  margin-left: 0.35rem;
}
</style>
